import * as THREE from "three";

type InitializedGeoPoint = GeoPoint & {
  scene: THREE.Scene;
  font: any;
  matLite: THREE.MeshBasicMaterial;
  message: string;
  position: THREE.Vector3;
  camera: THREE.PerspectiveCamera;
  sphere: THREE.Mesh;
  id: string;
};

export default class GeoPoint {
  scene: THREE.Scene;
  font: any;
  color = 0x006699;
  matLite: THREE.MeshBasicMaterial;
  message: string;
  fontSize = 0.3;
  position: THREE.Vector3;
  text: THREE.Mesh | null = null;
  camera: THREE.PerspectiveCamera;
  sphere: THREE.Mesh;
  id: string;

  constructor(
    position: THREE.Vector3,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    font: any,
    message: string,
    id: string
  ) {
    this.position = position;
    this.scene = scene;
    this.font = font;
    this.camera = camera;
    
    this.message = message;
    this.id = id;

    this.matLite = new THREE.MeshBasicMaterial( {
      color: this.color,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });

    const geometry = new THREE.SphereGeometry( 0.1, 32, 16 );
    const material = new THREE.MeshBasicMaterial( { color: 0xffff00 } );
    this.sphere = new THREE.Mesh( geometry, material );
    this.sphere.position.copy(this.position);
  }

  isInitialized(): this is InitializedGeoPoint {
    const missingProperties = [
      ["scene", this.scene],
      ["font", this.font],
      ["matLite", this.matLite],
      ["message", this.message],
      ["position", this.position],
      ["camera", this.camera],
      ["sphere", this.sphere],
      ["id", this.id]
    ].filter(([, value]) => value === null || value === undefined).map(([property]) => property);

    if (missingProperties.length > 0) {
      console.warn(`GeoPoint - missing initialized properties: ${missingProperties.join(", ")}`);
      return false;
    }

    return true;
  }

  setPosition(vec: THREE.Vector3) {
    if (!this.isInitialized()) return this;

    this.position = vec;
    return this;
  }

  setMessage(message: string) {
    if (!this.isInitialized()) return this;

    this.message = message;
    return this;
  }

  private renderMessage() {
    if (!this.isInitialized()) return;

    const shapes = this.font.generateShapes( this.message, this.fontSize );
    const textGeo = new THREE.ShapeGeometry( shapes );
    textGeo.computeBoundingBox();
    if (!textGeo.boundingBox) return;

    const xMid = - 0.5 * ( textGeo.boundingBox.max.x - textGeo.boundingBox.min.x );
    textGeo.translate( xMid, 0, 0 );
    this.text = new THREE.Mesh( textGeo, this.matLite );
    this.text.position.copy(this.position);
    this.text.position.y += 0.1 + this.fontSize;
    this.scene.add( this.text );
  }

  render() {
    if (!this.isInitialized()) return this;

    this.scene.add( this.sphere );

    if (this.message) {
      this.renderMessage();
    }

    return this;
  }

  update() {
    if (!this.isInitialized()) return;

    if (this.message && this.text) {
      this.text.lookAt(this.camera.getWorldPosition(
        this.position
      ));
    }
  }

  dispose (disposeResources = true) {
    if (!this.isInitialized()) return this;

    if (this.text) {
      this.scene.remove(this.text);
      this.text.geometry.dispose();

      if (disposeResources) {
        if (Array.isArray(this.text.material)) {
          this.text.material.forEach(material => material.dispose());
        } else {
          this.text.material.dispose();
        }
      }

      this.text = null;
    }

    this.scene.remove(this.sphere);

    if (disposeResources) {
      this.sphere.geometry.dispose();

      if (Array.isArray(this.sphere.material)) {
        this.sphere.material.forEach(material => material.dispose());
      } else {
        this.sphere.material.dispose();
      }
    }

    return this;
  }
}
